import { json } from "react-router-dom";
import { AxiosRequestConfig } from "axios";

import { RouteErrors } from "routes/data-routes/types";

import { COUPON_ERROR_CODES } from "v2/dataroutes/BillingData";

import scraperApi, { ApiError, ApiErrorResponse, ChargebeeApiErrorResponse } from "api";
import { BillingPath } from "v2/pages/billing/BillingPath";
import { ActiveSubscriptionDataRouteType } from "routes/data-routes/billing-data/active-subscription/route.meta";


type ChangeSubscriptionActionArgs = {
  targetPlanSlug: string;
  coupons?: string[];
  keepScheduledChanges: boolean;
};

export const changeSubscriptionAction: (args: ChangeSubscriptionActionArgs, opts?: AxiosRequestConfig) => ReturnType<NonNullable<ActiveSubscriptionDataRouteType["route"]["action"]>> =
  async ({
           targetPlanSlug,
           coupons,
           keepScheduledChanges
         }: ChangeSubscriptionActionArgs,
         opts?: AxiosRequestConfig
  ) => {
    try {
      return {
        activeSubscription: await scraperApi.subscription.update(targetPlanSlug, coupons, keepScheduledChanges, opts)
      };
    } catch (err) {
      const error = err as ApiError<ApiErrorResponse>;
      if (error.error_code === "chargebee_error") {
        const chargebeeError = error.details as ChargebeeApiErrorResponse;
        if (chargebeeError.chargebee_api_error_code === "payment_processing_failed") {
          return {
            // TODO both chargebeeError.message and chargebeeError.details hold useful information. we should somehow return both and display it to the user
            error: {
              taggedMessage: {
                message: chargebeeError.message || "Payment processing failed"
              }
            }
          }
        }
      }

      if (error.error_code && COUPON_ERROR_CODES.has(error.error_code)) {
        return {
          error: {
            taggedMessage: {
              message: error.message || "The coupon code is invalid or cannot be applied to this plan."
            }
          }
        };
      }

      if (error.error_code === "billing_error") {
        const details = error.details as { error_code?: string } | undefined;
        if (details?.error_code === "err_no_payment_method") {
          // returning proper responses here with an error status, so react-router won't revalidate/reload everything immediately
          // must not throw errors here, otherwise the router will go to the error path
          return json<RouteErrors.RouterActionError>(
            {
              error: {
                message: "Please add a payment method before changing your plan.",
                meta: {
                  title: "Payment method required",
                  buttonText: "Add Payment Method",
                  redirectTo: BillingPath + "/details#payment-method"
                }
              },
            },
            400
          );
        }
        if (details?.error_code === "err_no_billing_address") {
          // returning proper responses here with an error status, so react-router won't revalidate/reload everything immediately
          // must not throw errors here, otherwise the router will go to the error path
          return json<RouteErrors.RouterActionError>(
            {
              error: {
                message: "Please add a billing address before changing your plan.",
                meta: {
                  title: "Billing address required",
                  buttonText: "Add Billing Address",
                  redirectTo: BillingPath + "/details#billing-address"
                }
              }
            },
            400
          );
        }
      }

      // bubble up
      throw err;
    }
  };
